import { inject, Injectable } from '@angular/core';
import {
    collection,
    collectionData,
    deleteDoc,
    doc,
    Firestore,
    query,
    setDoc,
    updateDoc,
    where
} from '@angular/fire/firestore';
import { Member, Collections, MEMBER_MESSAGES } from '@ffx/common';
import { combineLatest, from, Observable, of, switchMap, take } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';

@Injectable({
    providedIn: 'root'
})
export class MemberService {
    private fireStore = inject(Firestore);

    addMember(member: Member, projectId: string): Observable<void | null> {
        // The db collections we will use
        const userCollection = collection(this.fireStore, Collections.users);
        const roleCollection = collection(this.fireStore, Collections.roles);
        const projectRoleUserCollection = collection(this.fireStore, Collections.projectRoleUser);

        // Get the user's details from the user document by their email used to check if the user is part of feature flex
        const getUserByEmailQuery = query(userCollection, where('email', '==', member.email));

        return collectionData(getUserByEmailQuery, { idField: 'id' }).pipe(
            take(1),
            switchMap((users) => {
                if (!users.length) {
                    throw new Error(MEMBER_MESSAGES.USER_DOES_NOT_EXIST);
                } else {
                    const userId = users[0].id;

                    // Get user's roleId by role name
                    const getRoleByNameQuery = query(roleCollection, where('name', '==', member.role));

                    // Get the user's role on the project used to check if the user is already part of the project
                    const getProjectRoleUserQuery = query(
                        projectRoleUserCollection,
                        where('projectId', '==', projectId),
                        where('userUid', '==', userId)
                    );

                    return combineLatest({
                        userId: of(userId),
                        roles: collectionData(getRoleByNameQuery, { idField: 'id' }).pipe(take(1)),
                        projectRoleUsers: collectionData(getProjectRoleUserQuery, { idField: 'id' }).pipe(take(1))
                    }).pipe(
                        switchMap(({ userId, roles, projectRoleUsers }) => {
                            if (projectRoleUsers.length) {
                                throw new Error(MEMBER_MESSAGES.USER_EXISTS_ALREADY);
                            }

                            const roleId = roles[0]?.id;
                            if (!roleId) {
                                throw new Error('Role not found.');
                            }

                            const docId = uuidv4();
                            const docRef = doc(this.fireStore, `${Collections.projectRoleUser}/${docId}`);
                            return from(
                                setDoc(docRef, {
                                    projectId,
                                    roleId,
                                    userUid: userId
                                })
                            );
                        })
                    );
                }
            })
        );
    }

    deleteMember(projectRoleUserId: string): Observable<void> {
        // Delete the user from this project
        const projectRoleUserRef = doc(this.fireStore, `${Collections.projectRoleUser}/${projectRoleUserId}`);
        return from(deleteDoc(projectRoleUserRef));
    }

    updateRole(member: Member): Observable<void> {
        // Get the roleId from the db by its name ('Member' or 'Owner')
        const roleCollection = collection(this.fireStore, Collections.roles);
        const getRoleByNameQuery = query(roleCollection, where('name', '==', member.role));
        return collectionData(getRoleByNameQuery, { idField: 'id' }).pipe(
            take(1),
            switchMap((roles) => {
                if (!roles.length) {
                    throw new Error('Role not found.');
                }

                // Now that we have the roleId, the user's roleId in the projectRoleUser document can be updated
                const roleId = roles[0].id;
                const projectRoleUserRef = doc(
                    this.fireStore,
                    `${Collections.projectRoleUser}/${member.projectRoleUserId}`
                );

                return from(updateDoc(projectRoleUserRef, { roleId }));
            })
        );
    }

    getUsersAndRolesByProjectId(projectId: string): Observable<any> {
        // Get the different roles (by roleId) and their names from the db
        const rolesCollection = collection(this.fireStore, Collections.roles);
        const getRolesQuery = query(rolesCollection);

        // Get this project's users (by userUid) and their roles (by roleId) from the db
        const projectRoleUserCollection = collection(this.fireStore, Collections.projectRoleUser);
        const getProjectRoleUserByProjectIdQuery = query(
            projectRoleUserCollection,
            where('projectId', '==', projectId)
        );

        return combineLatest({
            roles: collectionData(getRolesQuery, { idField: 'id' }).pipe(take(1)),
            projectRoleUsers: collectionData(getProjectRoleUserByProjectIdQuery, {
                idField: 'id'
            }).pipe(take(1))
        }).pipe(
            switchMap(({ roles, projectRoleUsers }) => {
                // Now that we have the userUids with their roleIds, query the user document to get the user's emails for display
                const projectRoleUserUids = projectRoleUsers.map((projectRoleUser) => projectRoleUser['userUid']);
                const userCollection = collection(this.fireStore, Collections.users);
                // Note: This query will only return up to 30 results
                const getUserByProjectRoleUserUidQuery = query(userCollection, where('id', 'in', projectRoleUserUids));

                return collectionData(getUserByProjectRoleUserUidQuery, { idField: 'id' }).pipe(
                    take(1),
                    // Save all the required details about the users of this project to the store
                    switchMap((users) => {
                        const foundUsers = projectRoleUsers.map((project: any) => {
                            const role = roles.find((role: any) => role.id == project.roleId);
                            const user = users.find((user: any) => user.id === project.userUid);
                            return {
                                email: user ? user['email'] : '',
                                role: role ? role['name'] : 'None',
                                editing: false,
                                projectRoleUserId: project?.id || ''
                            };
                        });

                        return of(foundUsers);
                    })
                );
            })
        );
    }

    getProjectRoleUserId(projectId: string, userUid: string): Observable<any> {
        const projectRoleUserCollection = collection(this.fireStore, Collections.projectRoleUser);
        const getProjectRoleUserQuery = query(
            projectRoleUserCollection,
            where('projectId', '==', projectId),
            where('userUid', '==', userUid)
        );

        return collectionData(getProjectRoleUserQuery, {
            idField: 'id'
        });
    }
}
